/**
 * Blog Sidebar Modal Component
 * Handles the opening, closing, and interactions of the blog sidebar panel
 */

class BlogSidebarModal extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('[data-blog-sidebar-trigger]');
    this.modal = this.querySelector('[data-blog-sidebar-modal]');
    this.overlay = this.querySelector('[data-blog-sidebar-overlay]');
    this.closeButton = this.querySelector('[data-blog-sidebar-close]');
    
    this.init();
  }

  init() {
    if (!this.trigger || !this.modal || !this.overlay) return;

    // Bind event listeners
    this.trigger.addEventListener('click', this.open.bind(this));
    this.closeButton?.addEventListener('click', this.close.bind(this));
    this.overlay.addEventListener('click', this.close.bind(this));
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Trap focus within modal when open
    this.modal.addEventListener('keydown', this.trapFocus.bind(this));
  }

  open() {
    this.modal.classList.add('active');
    this.overlay.classList.add('active');
    document.body.classList.add('blog-sidebar-open');
    
    // Set focus to close button for accessibility
    setTimeout(() => {
      this.closeButton?.focus();
    }, 100);

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('blog-sidebar:opened', {
      bubbles: true,
      detail: { modal: this }
    }));
  }

  close() {
    this.modal.classList.remove('active');
    this.overlay.classList.remove('active');
    document.body.classList.remove('blog-sidebar-open');
    
    // Return focus to trigger button
    setTimeout(() => {
      this.trigger?.focus();
    }, 100);

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('blog-sidebar:closed', {
      bubbles: true,
      detail: { modal: this }
    }));
  }

  isOpen() {
    return this.modal.classList.contains('active');
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  trapFocus(e) {
    if (e.key !== 'Tab' || !this.isOpen()) return;

    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }
}

// Register custom element
if (!customElements.get('blog-sidebar-modal')) {
  customElements.define('blog-sidebar-modal', BlogSidebarModal);
}

// Animation helper for smooth scroll lock
const originalStyle = {
  overflow: '',
  paddingRight: ''
};

// Save original body style when modal opens
document.addEventListener('blog-sidebar:opened', () => {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  originalStyle.overflow = document.body.style.overflow;
  originalStyle.paddingRight = document.body.style.paddingRight;
  
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
});

// Restore original body style when modal closes
document.addEventListener('blog-sidebar:closed', () => {
  document.body.style.overflow = originalStyle.overflow;
  document.body.style.paddingRight = originalStyle.paddingRight;
});
